<template>
<div v-if="assignedConnector" class="assigned-item-preview" :class="{ correction: assignedConnector.correction }">

  <div v-if="connectorType === 'cost'" class="assigned-item-preview--info">
  <!-- COST -->
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Number') }}</div>
      <div class="info-box--data">{{ assignedConnector.number }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Description') }}</div>
      <div class="info-box--data data-description">
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' }, className : 'os-theme-dark os-theme-gekko' }">
          <div v-if="assignedConnector.description" class="data-value">{{ assignedConnector.description }}</div>
          <div v-else class="data-placeholder">{{ $gettext('Empty') }}</div>
        </overlay-scrollbars>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Amount') }}</div>
      <div class="info-box--data">€ {{ assignedConnector.amount | formatNumber }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('VAT') }}</div>
      <div v-if="!assignedConnector.tax_exempt && !assignedConnector.btw_reversed_charge" class="info-box--data">€ {{ assignedConnector.total_tax | formatNumber }}</div>
      <div v-if="assignedConnector.tax_exempt" class="info-box--data">{{ $gettext('Tax exempt') }}</div>
      <div v-if="assignedConnector.btw_reversed_charge" class="info-box--data">{{ $gettext('Reversed charge') }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Date') }}</div>
      <div class="info-box--data">{{ assignedConnector.date }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Type') }}</div>
      <div class="info-box--data data-icon" 
        :class="{'no-icon': !assignedConnector.cost_type}"
        v-b-tooltip.hover :title="iconText"
        v-html="iconCode"
      />
    </div>
  </div>

  <div v-if="connectorType === 'invoice' && !assignedConnector.correction" class="assigned-item-preview--info">
  <!-- INVOICE -->
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Invoice Number') }}</div>
      <div class="info-box--data">{{ assignedConnector.number }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Customer') }}</div>
      <div class="info-box--data">
        <div v-if="assignedConnector.recipient_customer" class="data-value">{{ assignedConnector.recipient_customer.name }}</div>
        <div v-else class="data-placeholder">{{ $gettext('None') }}</div>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Project') }}</div>
      <div class="info-box--data">
        <div v-if="assignedConnector.project" class="data-value">{{ assignedConnector.project.name }}</div>
        <div v-else class="data-placeholder">{{ $gettext('None') }}</div>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Date') }}</div>
      <div class="info-box--data">{{ assignedConnector.invoice_date }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Amount') }}</div>
      <div class="info-box--data">€ {{ assignedConnector.total_amount | formatNumber }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('VAT') }}</div>
      <div v-if="!assignedConnector.vat_reversed_charge" class="info-box--data">€ {{ assignedConnector.total_tax | formatNumber }}</div>
      <div v-else class="info-box--data">{{ $gettext('Reversed charge') }}</div>

    </div>
    <div v-if="assignedConnector.message" class="info-box info-box--message">
      <div class="info-box--label">{{ $gettext('Message') }}</div>
      <div class="info-box--data">
        <p>{{ assignedConnector.message }}</p>
      </div>
    </div>
  </div>

  <div v-if="!assignedConnector.correction" class="assigned-item-preview--document">
  <!-- INVOICE/COST PROOF -->
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Proof') }}</div>
      <div class="info-box--data">
        <BankDocumentPreview :file-path="previewFile" />
      </div>
    </div>
  </div>

  <div v-if="connectorType === 'invoice' && assignedConnector.correction" class="assigned-item-preview--info">
  <!-- CORRECTION -->
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Name') }}</div>
      <div class="info-box--data">
        <div v-if="assignedConnector.correction.name" class="data-value">{{ assignedConnector.correction.name }}</div>
        <div v-else class="data-placeholder">{{ $gettext('None') }}</div>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Correction Period') }}</div>
      <div class="info-box--data">{{ assignedConnector.correction.timeframe }}</div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Customer') }}</div>
      <div class="info-box--data">
        <div v-if="assignedConnector.recipient_customer" class="data-value">{{ assignedConnector.recipient_customer.name }}</div>
        <div v-else class="data-placeholder">{{ $gettext('None') }}</div>
      </div>
    </div>
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Project') }}</div>
      <div class="info-box--data">
        <div v-if="assignedConnector.project" class="data-value">{{ assignedConnector.project.name }}</div>
        <div v-else class="data-placeholder">{{ $gettext('None') }}</div>
      </div>
    </div>
    <div v-if="assignedConnector.message" class="info-box info-box--message">
      <div class="info-box--label">{{ $gettext('Message') }}</div>
      <div class="info-box--data">
        <p>{{ assignedConnector.message }}</p>
      </div>
    </div>
  </div>

  <div v-if="assignedConnector.correction && correctionLoaded">
  <!-- CORRECTION VAT LINES -->
    <table class="table b-table gk-table gk-table--responsive gk-table--striped gk-table--headed">
      <thead>
        <td><small >{{ $gettext('Total(inc. VAT)') }}</small></td>
        <td><small >{{ $gettext('Total (exc. VAT)') }}</small></td>
        <td><small >{{ $gettext('Tax Rate') }}</small></td>
      </thead>
      <tbody>
        <tr v-for="(line, index) in assignedConnector.invoice_lines" :key="'corr-line-' + index">
          <td><label name="name" type="text">{{ line.amount | formatNumber }}</label></td>
          <td><span v-if="line.total_tax">{{ line.total_tax | formatNumber }}</span></td>
          <td>
            <span v-if="!line.vat_exempt">{{ line.tax_rate }}%</span>
            <span v-else>{{ $gettext('VAT Exempt') }}</span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td class="text-right">{{ $gettext('Subtotal') }}</td>
          <td>{{ assignedConnector.calculated_subtotal | formatNumber }}</td>
          <td></td>
        </tr>
        <tr v-for="(this_total, this_vat) in assignedConnector.calculated_vat_lines" :key="'corr-vat-line-' + this_vat">
          <td class="text-right">{{ this_vat }}% {{ $gettext('VAT') }}</td>
          <td v-if="!assignedConnector.vat_reversed_charge">{{ this_total | formatNumber }}</td>
          <td v-else>{{ $gettext('reverse charged') }}</td>
        </tr>
        <tr>
          <td class="text-right">{{ $gettext('Total') }}</td>
          <td>{{ assignedConnector.calculated_total | formatNumber }}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <div v-if="assignedConnector.correction" class="assigned-item-preview--document">
  <!-- CORRECTION FILES -->
    <div class="info-box">
      <div class="info-box--label">{{ $gettext('Files') }}</div>
      <div class="info-box--data">
        <AttachedDocumentList :object-id="assignedConnector.correction.id"/>
      </div>
    </div>
  </div>

</div>
</template>

<script>
import { mapGetters } from "vuex";
import BankDocumentPreview from "@/components/bank/ui/BankDocumentPreview"
import AttachedDocumentList from "@/components/documents/AttachedDocumentList"
import CostTypeService from "@/store/services/costtype.service.js";
export default {
  name: "BankMatchedItemPreview",
  components: {
    BankDocumentPreview, AttachedDocumentList
  },
  props: {
    assignedConnector: {
      type: Object,
      default: () => {}
    },
    connectorType: {
      type: String,
      default: null
    },
  },
  data () {
    return {
      costTypes: {},
      costsLoaded: false,
      correctionLoaded: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: 'main/getCurrentCompany',
      getCompanyId: 'main/getCompanyId'
    }),
    previewFile () {
      let filePath = ''
      if (this.connectorType === 'cost') {
        filePath = this.assignedConnector.image
      }
      if (this.connectorType === 'invoice') {
        filePath = this.assignedConnector.pdf_file
      }
      return filePath
    },
    iconCode () {
      let iconCode = this.$gettext('None')
      if (this.connectorType === 'cost' && this.costsLoaded) {
        if (this.assignedConnector.cost_type && this.costTypes[this.assignedConnector.cost_type]) {
          iconCode = "&#x" + this.costTypes[this.assignedConnector.cost_type].icon + ";"
        }
      }
      return iconCode
    },
    iconText () {
      let iconText = this.$gettext('No cost type selected')
      if (this.connectorType === 'cost' && this.costsLoaded) {
        if (this.assignedConnector.cost_type) iconText = this.costTypes[this.assignedConnector.cost_type].name
      }
      return iconText
    }
  },
  created () {
    if (this.connectorType === 'cost') {
      this.getCostTypes()
    } else if (this.assignedConnector.correction) {
      this.calculateVatLines()
    }
  },
  methods: {
    calculateVatLines () {
      let vatLines = {}
      let subtotal = 0
      let total = 0
      let a_line = {}
      
      if (this.assignedConnector.vat_reversed_charge) {
        vatLines[21] = 0
        for (let i in this.assignedConnector.invoice_lines) {
          subtotal = subtotal + parseFloat(a_line.subtotal_amount)
          a_line = this.assignedConnector.invoice_lines[i]
          total = total + parseFloat(a_line.amount)
          vatLines[21] += Math.round((a_line.amount * ( 21 / 100))*100)/100
        }
      } else {
        for (let i in this.assignedConnector.invoice_lines) {
          a_line = this.assignedConnector.invoice_lines[i]
          subtotal = subtotal + parseFloat(a_line.subtotal_amount)
          total = total + parseFloat(a_line.amount)
          if (parseFloat(a_line.amount) > 0 && !a_line.vat_exempt) {
            if (!vatLines[a_line.tax_rate]) {
              vatLines[a_line.tax_rate] = 0
            }
            vatLines[a_line.tax_rate] += parseFloat(a_line.amount) - Math.round(((parseFloat(a_line.amount) / ( 1 + a_line.tax_rate / 100)))*100)/100
          }
        }
      }
      subtotal = Math.round(subtotal * 100) / 100
      this.calculateRetention(subtotal)
        if (this.assignedConnector.retention_tax_amount) {
        total += parseFloat(this.assignedConnector.retention_tax_amount)
      }
      total = Math.round(total * 100) / 100 //make sure we have only 2 decimals
      
      this.assignedConnector.calculated_total = total
      this.assignedConnector.calculated_vat_lines = vatLines
      this.assignedConnector.calculated_subtotal = subtotal
      this.correctionLoaded = true
    },
    calculateRetention (subtotal) {
      if (this.currentCompany && this.currentCompany.country != 'ES') {
        this.assignedConnector.retention_tax_amount = undefined
        this.assignedConnector.retention_tax_percentage = undefined
        return
      }
      this.assignedConnector.retention_tax_amount = -Math.round(subtotal * this.assignedConnector.retention_tax_percentage) / 100
      if (!this.assignedConnector.retention_tax_amount) {
        this.assignedConnector.retention_tax_amount = 0
      }
    },
    getCostTypes () {
      CostTypeService.list({ company: this.getCompanyId }).then((response) => {
        for (var i = 0; i < response.data.length; i++) {
          this.costTypes[response.data[i].id] = response.data[i]
        }
        this.costsLoaded = true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.assigned-item-preview {
  display: flex;
  &.correction {
    flex-direction: column;
    .assigned-item-preview--info {
      flex-wrap: wrap;
      flex-direction: row;
      .info-box {
        width: 50%;
      }
    }
    .assigned-item-preview--info,
    .assigned-item-preview--document {
      width: 100%;
    }
  }
  &--info {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    width: 35%;
  }
  &--document {
    flex: 1;
    padding: 0.5rem;
    .info-box { height: 100%; }
    .info-box--data {
      padding: 0.5rem;
      border-radius: $radiusMd;
      background: $inputBackgroundColor;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    }
  }
  .info-box {
    display: flex;
    flex-direction: column;
    &:not(:last-child) { margin-bottom: 1rem; }
    &--label {
      font-size: 12px;
      text-transform: uppercase;
      color: $colorUnassigned;
      margin-bottom: 0.25rem;
    }
    &--data {
      height: 100%;
      &.data-description {
        font-size: 14px;
        .os-host { height: auto; max-height: 100px; }
      }
      &.data-icon {
        width: 25px; 
        &.no-icon { color: $colorUnassigned; }
      }
      .data-placeholder { color: $colorUnassigned; }
    }
    &--message {
      width: 100% !important;
      p { margin: 0; }
    }
  }
}
</style>
